import React, { useCallback, useEffect, useState } from 'react'
import { MyImage } from '~/components/ui/MyImage'
import useInfiniteScroll from '~/hooks/useInfiniteScroll'
import {
  PostTypes,
  getBackImageList_squareModule_postBackImagePage_edges_node,
  getBackImageList,
  getBackImageListVariables,
} from '~/graphqls/types'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import { MyEmpty } from '~/components/ui/MyEmpty'
import { GET_BACK_IMAGE_LIST } from '~/graphqls/gqls'
import { getGraphData, getLocalStorage, removeLocalStorage } from '~/utils'
import { PAGE_SIZE } from '~/configs'
import { useMasonry } from '~/hooks/useMasonry'
import { useRouter } from 'next/router'
import Link from 'next/link'
import { MyAvatar } from '~/components/ui/MyAvatar'
import { PreviewBackImageModal } from './PreviewBackImageModal'

export const BackImageList: React.FC<{
  post: PostTypes
  backImageList: getBackImageList_squareModule_postBackImagePage_edges_node[]
}> = ({ post, backImageList: _backImageList }) => {
  const [details, setDetails] =
    useState<getBackImageList_squareModule_postBackImagePage_edges_node | null>(
      null
    )
  const router = useRouter()
  const [nextPage, setNextPage] = useState(false)
  const [backImageList, setBackImageList] = useState<
    getBackImageList_squareModule_postBackImagePage_edges_node[]
  >(_backImageList || [])
  const queryList = useImperativeQuery<
    getBackImageList,
    getBackImageListVariables
  >(GET_BACK_IMAGE_LIST)

  const { InfiniteScollComp, list } = useInfiniteScroll<
    getBackImageList_squareModule_postBackImagePage_edges_node,
    getBackImageList,
    getBackImageListVariables
  >({
    queryDoc: GET_BACK_IMAGE_LIST,
    initData: backImageList,
    hasNextPage: nextPage,
    extraQueryParams: {
      postId: post.id,
    },
    hasNextPageExtractor: res => {
      return (
        res.data.squareModule?.postBackImagePage?.pageInfo?.hasNextPage || false
      )
    },
    dataExtractor: res => {
      return getGraphData(res.data.squareModule?.postBackImagePage?.edges || [])
    },
  })
  const { columnList, columnWidth, columnGutter } =
    useMasonry<getBackImageList_squareModule_postBackImagePage_edges_node>({
      data: list,
      sizeLoader: (
        it: getBackImageList_squareModule_postBackImagePage_edges_node
      ) => {
        if (!it?.height || !it?.width) return 1
        return it?.height / it?.width
      },
      column: 6,
    })
  const init = () => {
    queryList({
      first: PAGE_SIZE,
      offset: 0,
      postId: post.id,
    })
      .then(res => {
        setBackImageList(
          getGraphData(res.data.squareModule?.postBackImagePage?.edges || [])
        )
        setNextPage(
          res.data.squareModule?.postBackImagePage?.pageInfo?.hasNextPage ||
            false
        )
      })
      .catch(err => {
        console.error(err)
      })
  }

  const percentage = useCallback(
    (data: getBackImageList_squareModule_postBackImagePage_edges_node) => {
      if (!data?.width || !data.height) return 100
      return (Number(data?.height) / Number(data?.width)) * 100
    },
    []
  )

  // 从其它页面进来定位到返图位置
  useEffect(() => {
    const isBackImageLocation = getLocalStorage('isBackImageLocation')
    if (isBackImageLocation) {
      const ele = document.getElementById('backImageAndComment')
      if (ele) ele.scrollIntoView()
    }
    removeLocalStorage('isBackImageLocation')
  }, [])

  if (!list.length) {
    return null
  }
  return (
    <div
      className="w-full mt-6 pt-6 border-t border-t-neutral-200"
      id="backImageAndComment"
    >
      {list.length > 0 ? (
        <div className="mb-6">
          <div className="flex justify-between items-start">
            {columnList?.map((item, index) => (
              <div
                key={index}
                style={{ width: columnWidth }}
                className="selection:bg-transparent"
              >
                {item?.map(it => (
                  <div
                    id={it.id}
                    style={{
                      marginBottom: columnGutter,
                    }}
                    key={it.id}
                    className="cursor-pointer"
                    onClick={() => setDetails(it)}
                  >
                    <div
                      className="w-full h-0 relative overflow-hidden rounded-[8px]"
                      style={{ paddingBottom: `${percentage(it)}%` }}
                    >
                      <div className="absolute left-0 top-0 bottom-0 right-0">
                        <MyImage
                          dark
                          className="h-full w-full rounded-[8px]"
                          src={it.artWorkUrl}
                        />
                        <div className="w-full absolute bottom-2 left-0 z-10">
                          <div
                            className="px-2 py-1 rounded-[8px] mx-2"
                            style={{ background: 'rgba(0, 0, 0, 0.32)' }}
                            onClick={e => e.stopPropagation()}
                          >
                            <Link href={`/user/${it.userInfo?.uid}`}>
                              <div className="flex items-center">
                                <MyAvatar
                                  className="shrink-0"
                                  image={it.userInfo?.avatarUrl || ''}
                                  isVip={it.userInfo?.memberDetail?.hold}
                                  size={16}
                                  hiddenIcon
                                ></MyAvatar>
                                <span className="text-neutral-800 text-xs max-w-[140px] line-clamp-1 ml-2">
                                  {it.userInfo?.nickname}
                                </span>
                              </div>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
          {InfiniteScollComp}
        </div>
      ) : (
        <div className="p-6 mb-6 rounded-xl bg-[#1E1F21]">
          <MyEmpty image="/images/empty-black.png" />
        </div>
      )}
      <PreviewBackImageModal
        visible={!!details?.id}
        onCancel={() => setDetails(null)}
        product={details}
        onSuccess={init}
      />
    </div>
  )
}
